function formatValue(formatter: Intl.NumberFormat, value: number | bigint, negativeZero: string, zero: string): string {
    const formatted = formatter.format(value);
    return formatted === negativeZero ? zero : formatted;
}

function sanitizeNegativeZero(formatter: Intl.NumberFormat): (value: number | bigint) => string {
    const negativeZero = formatter.format(-0);
    const zero = formatter.format(0);

    return (value) => formatValue(formatter, value, negativeZero, zero);
}

// Wraps Intl.NumberFormat in a Proxy to prevent values rounding to zero (e.g. -0.225) from rendering as -0.
export function createNumberFormatter(options?: Intl.NumberFormatOptions): Intl.NumberFormat {
    const formatter = Intl.NumberFormat("en-us", options);
    const format = sanitizeNegativeZero(formatter);

    const handler: ProxyHandler<Intl.NumberFormat> = {
        get(target, prop) {
            if (prop === "format") {
                return format;
            }
            const value = Reflect.get(target, prop, target);
            return typeof value === "function" ? value.bind(target) : value;
        }
    };

    const proxy = new Proxy(formatter, handler);
    return proxy;
}

export const formatterDollarUSNoDecimal = createNumberFormatter({
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    signDisplay: "auto"
});

export const formatterDollarUSNoDecimalSignAlways = createNumberFormatter({
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    signDisplay: "always"
});

export const formatterDollarUSNoDecimalNoGrouping = createNumberFormatter({
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    signDisplay: "auto",
    useGrouping: false
});

export const formatterDollarUS = createNumberFormatter({
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
    minimumFractionDigits: 2
});

export const formatterDollarUSNoSign = createNumberFormatter({
    style: "currency",
    currency: "USD",
    signDisplay: "never",
    maximumFractionDigits: 2,
    minimumFractionDigits: 2
});

export const formatterInt = createNumberFormatter({
    style: "decimal",
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    signDisplay: "auto"
});

export const formatterIntNoGrouping = createNumberFormatter({
    style: "decimal",
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    signDisplay: "auto",
    useGrouping: false
});

export const formatterDecimal = createNumberFormatter({
    style: "decimal",
    maximumFractionDigits: 2,
    minimumFractionDigits: 2
});

export const formatterDecimalUnlimitedFractionDigits = createNumberFormatter({
    style: "decimal",
});

export const formatterDecimalNoGrouping = createNumberFormatter({
    style: "decimal",
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    useGrouping: false
});

export const formatterPercent = createNumberFormatter({
    style: "percent",
    maximumFractionDigits: 1,
    minimumFractionDigits: 1
});

export const formatterPercentNoGrouping = createNumberFormatter({
    style: "percent",
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
    useGrouping: false
});

export const formatterPercentAutoSign = createNumberFormatter({
    style: "percent",
    signDisplay: "auto",
    maximumFractionDigits: 1,
    minimumFractionDigits: 1
});

export const formatterPercentWithSign = createNumberFormatter({
    style: "percent",
    signDisplay: "always",
    maximumFractionDigits: 1,
    minimumFractionDigits: 1
});

export const formatterPercentTwoDecimals = Intl.NumberFormat("en-us", {
    style: "percent",
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
});

export const formatterDateUSFull = Intl.DateTimeFormat("en-us", {
    dateStyle: "full"
});

export const formatterDateTimeUS = (input: string | Date): string => {
    const date = typeof input === "string" ? new Date(input) : input;

    const options: Intl.DateTimeFormatOptions = {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true
    };

    return date.toLocaleString("en-US", options);
};

export const formatterTimeUS = (input: string | Date): string => {
    const date = typeof input === "string" ? new Date(input) : input;

    const options: Intl.DateTimeFormatOptions = {
        hour: "numeric",
        minute: "2-digit",
        hour12: true
    };

    return date.toLocaleString("en-US", options);
};

export const formatterTimeHH24MI = (input: string | Date): string => {
    const date = typeof input === "string" ? new Date(input) : input;

    const options: Intl.DateTimeFormatOptions = {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    };

    return date.toLocaleString("en-US", options).replace(":", "");
};

export const formatterDateISO = (input: string | Date): string => {
    const date = typeof input === "string" ? new Date(input) : input;

    return date.toISOString().split("T")[0]?.replace(/-/g, "") ?? "";
};

export const formatExportTimestampSlug = (date: Date): string => {
    const mm = date.getMonth() + 1;
    const dd = date.getDate();
    const yyyy = date.getFullYear();
    return `${mm}_${dd}_${yyyy}_${formatterTimeHH24MI(date)}`;
};

export function formatterEnumToTitle(enumValue: string): string {
    let specialsReplaced = enumValue;

    if (enumValue.includes("_")) {
        // if snake case
        specialsReplaced = specialsReplaced.replaceAll("_", " ").toLowerCase();
    } else if (enumValue.toUpperCase() != enumValue) {
        // if camel case
        specialsReplaced = specialsReplaced.replace(/([A-Z])/g, " $1").toLowerCase();
    } else {
        // just all caps, convert to title and move on
        return enumValue.charAt(0).toUpperCase() + enumValue.substring(1).toLowerCase();
    }

    return specialsReplaced.split(' ').map((x) => (
            x.charAt(0).toUpperCase() + x.substring(1))).join(' ');
}

export function getMonthUSFull(date: Date): string {
    const parts = formatterDateUSFull.formatToParts(date);
    let month = parts.find(part => part.type == "month")?.value;
    if (month == undefined) {
        month = `${date.getMonth() + 1}`;
    }

    return month;
}

export function formatterGlName(glName: string, glNumber: string | number | null | undefined): string {
    let ret = glName;
    if (glNumber !== null && glNumber !== undefined) {
        if (typeof glNumber === "number" || glNumber.trim().length > 0) {
            ret += ` (${glNumber})`;
        }
    }

    return ret;
}
